import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { hollandTypes, careerTracks } from '@/mocks/careerData';
import { recommendCareer } from '@/lib/careerRecommend';
import { hollandQuestions } from '@/mocks/hollandQuestions';
import { valueQuestions } from '@/mocks/valueQuestions';
import ResultJobs from './ResultJobs';
import ResultSubjects from './ResultSubjects';
import CareerProReport from './CareerProReport';
import { buildCareerReport } from '@/lib/careerAnalysis';
import { computeAnswerConsistency } from '@/lib/consistency';
import { useTestResult } from '@/hooks/useTestResult';
import SaveStatusBanner from '@/components/feature/SaveStatusBanner';
import type { TestAnswers } from '@/pages/career-test/page';

interface Props {
  answers: TestAnswers;
  onRetake: () => void;
}

const HOLLAND_MAX_SCORE = hollandQuestions.filter(q => q.type === 'R').length * 10;

function computeHollandScores(holland: Record<string, number>) {
  const scores: Record<string, number> = { R: 0, I: 0, A: 0, S: 0, E: 0, C: 0 };
  hollandQuestions.forEach(q => {
    const val = holland[q.id];
    if (val !== undefined) scores[q.type] += val;
  });
  return scores;
}

const VALUE_MAX_SCORE = valueQuestions.filter(q => q.type === 'achievement').length * 10;

function computeValueScores(value: Record<string, number>) {
  const scores: Record<string, number> = {};
  valueQuestions.forEach(q => {
    const val = value[q.id];
    if (val !== undefined) scores[q.type] = (scores[q.type] || 0) + val;
  });
  return scores;
}

function getTopHolland(scores: Record<string, number>) {
  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([k]) => k);
}

function getTopValues(scores: Record<string, number>) {
  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([k]) => k);
}

const hollandLabels: Record<string, string> = {
  R: '현실형', I: '탐구형', A: '예술형', S: '사회형', E: '진취형', C: '관습형'
};

const valueLabels: Record<string, string> = {
  achievement: '성취감',
  helping: '사회공헌',
  creativity: '창의·표현',
  intellectual: '지적 탐구',
  leadership: '영향력',
  stability: '안정·전문성',
};

// 흥미 점수 상대 수준 해석 (6개 유형 평균 대비 상대 비교)
function getRelativeScoreLevel(scores: Record<string, number>, code: string) {
  const vals = Object.values(scores);
  const mean = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  const score = scores[code] || 0;
  if (mean <= 0) return { label: '보통', className: 'bg-background-200 text-foreground-600' };
  const devPct = ((score - mean) / mean) * 100;
  if (devPct >= 25) return { label: '매우 높음', className: 'bg-primary-500 text-white' };
  if (devPct >= 15) return { label: '높음', className: 'bg-primary-100 text-primary-700' };
  if (devPct >= 0) return { label: '보통', className: 'bg-background-200 text-foreground-600' };
  return { label: '낮음', className: 'bg-background-100 text-foreground-500' };
}

export default function TestResult({ answers, onRetake }: Props) {
  const navigate = useNavigate();
  const { saveTestResult, saveStatus } = useTestResult();
  const hollandScores = computeHollandScores(answers.holland);
  const valueScores = computeValueScores(answers.value);
  const topHolland = getTopHolland(hollandScores);
  const topValues = getTopValues(valueScores);
  const maxHollandScore = Math.max(...Object.values(hollandScores), 0);

  const recommendation = recommendCareer(hollandScores, valueScores, HOLLAND_MAX_SCORE, VALUE_MAX_SCORE);
  const recommendedTrackIds = recommendation.trackIds;

  const topTrack = careerTracks.find(t => t.id === recommendedTrackIds[0]);
  const otherTracks = recommendedTrackIds.slice(1)
    .map(id => careerTracks.find(t => t.id === id))
    .filter(Boolean);

  const hollandType1 = hollandTypes.find(t => t.id === topHolland[0]);
  const hollandType2 = hollandTypes.find(t => t.id === topHolland[1]);

  const recommendedMajors = recommendation.majors.slice(0, 8);
  const firstMajor = recommendedMajors[0];
  const allJobs = recommendation.jobs;

  const proReport = buildCareerReport({
    hollandScores,
    valueScores,
    topHolland,
    topValues,
    hollandMax: HOLLAND_MAX_SCORE,
    valueMax: VALUE_MAX_SCORE,
  });

  const consistency = computeAnswerConsistency(answers.holland, answers.value);

  useEffect(() => {
    saveTestResult('career', '계열·진로 탐색 검사', null, {
      hollandScores,
      valueScores,
      topHolland,
      topValues,
      recommendedTrackIds,
      recommendedMajors: recommendedMajors.map((m) => m.name),
      allJobs: allJobs.slice(0, 10),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-3xl mx-auto">
      {/* Result Header */}
      <div className="bg-primary-500 rounded-2xl p-6 md:p-8 text-white text-center mb-6">
        <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-4">
          <i className="ri-trophy-line text-2xl text-white"></i>
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">
          나의 진로 결과지
        </h1>
        <p className="text-primary-100 text-sm">
          계열 · 전공 · 직업 · 선택과목까지 한 번에 분석한 결과예요
        </p>
      </div>

      <SaveStatusBanner status={saveStatus} />

      {/* 응답 일관성 분석 */}
      <div className={`rounded-2xl p-5 md:p-6 mb-4 border ${
        consistency.isConsistent
          ? 'bg-background-50 border-background-200'
          : 'bg-accent-50 border-accent-300'
      }`}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="font-semibold text-foreground-950 flex items-center gap-2">
            <i className={`${consistency.isConsistent ? 'ri-shield-check-line text-secondary-500' : 'ri-alert-line text-accent-500'}`}></i>
            응답 일관성 분석
          </h2>
          <span className={`text-sm font-bold px-3 py-1 rounded-full ${
            consistency.isConsistent
              ? 'bg-secondary-100 text-secondary-800'
              : 'bg-accent-500 text-white'
          }`}>
            {consistency.level} · {consistency.score}%
          </span>
        </div>
        <div className="w-full bg-background-200 rounded-full h-2.5 mb-3">
          <div
            className={`h-2.5 rounded-full transition-all duration-500 ${
              consistency.isConsistent ? 'bg-secondary-500' : 'bg-accent-500'
            }`}
            style={{ width: `${Math.min(consistency.score, 100)}%` }}
          ></div>
        </div>
        <p className="text-sm text-foreground-600 leading-relaxed mb-2">{consistency.comment}</p>
        <div className="flex flex-wrap gap-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-mental-health-line"></i> 흥미검사 일관성 {consistency.hollandScore}%
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-heart-line"></i> 가치관검사 일관성 {consistency.valueScore}%
          </span>
        </div>

        {!consistency.isConsistent && (
          <div className="mt-4 bg-white border border-accent-300 rounded-xl p-4">
            <p className="text-sm text-accent-800 font-semibold mb-2">
              <i className="ri-error-warning-line mr-1.5"></i>
              답변이 일관적이지 않아 결과의 정확도가 낮을 수 있어요.
            </p>
            <p className="text-xs text-accent-700 leading-relaxed mb-3">
              같은 내용을 다르게 물은 문항들에 답이 크게 엇갈렸어요. 검사 결과가 정확하려면
              스스로에 대해 솔직하고 정직하게 답하는 것이 가장 중요합니다. 잠시 시간을 두고
              마음을 편하게 가진 뒤 다시 검사해보는 것을 권장해요.
            </p>
            <button
              onClick={onRetake}
              className="whitespace-nowrap bg-accent-500 hover:bg-accent-600 text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line mr-2"></i>다시 검사하기
            </button>
          </div>
        )}
      </div>

      {/* Summary - 한눈에 보기 */}
      {topTrack && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div className="bg-background-50 border border-primary-200 rounded-xl p-4">
            <div className="text-xs text-foreground-500 mb-1 flex items-center gap-1.5">
              <i className="ri-road-map-line text-primary-500"></i> 추천 계열
            </div>
            <div className="font-bold text-foreground-950">{topTrack.name}</div>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-xl p-4">
            <div className="text-xs text-foreground-500 mb-1 flex items-center gap-1.5">
              <i className="ri-building-4-line text-secondary-500"></i> 대표 전공
            </div>
            <div className="font-bold text-foreground-950">{firstMajor?.name || '—'}</div>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-xl p-4">
            <div className="text-xs text-foreground-500 mb-1 flex items-center gap-1.5">
              <i className="ri-briefcase-4-line text-accent-500"></i> 대표 직업
            </div>
            <div className="font-bold text-foreground-950">{allJobs[0] || '—'}</div>
          </div>
        </div>
      )}

      {/* Holland Type */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
        <h2 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-mental-health-line text-primary-500"></i>
          나의 흥미 유형
        </h2>
        <div className="flex flex-wrap gap-3 mb-4">
          {topHolland.map((code, idx) => {
            const type = hollandTypes.find(t => t.id === code);
            const scorePct = maxHollandScore > 0 ? Math.round((hollandScores[code] / maxHollandScore) * 100) : 0;
            const level = getRelativeScoreLevel(hollandScores, code);
            return (
              <div key={code} className={`flex-1 min-w-[140px] rounded-xl p-4 ${
                idx === 0 ? 'bg-primary-50 border border-primary-200' : 'bg-background-100 border border-background-200'
              }`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {idx === 0 && <span className="text-xs bg-primary-500 text-white px-2 py-0.5 rounded-full">주유형</span>}
                  {idx === 1 && <span className="text-xs bg-background-300 text-foreground-600 px-2 py-0.5 rounded-full">보조유형</span>}
                  {idx === 0 && <span className={`text-xs px-2 py-0.5 rounded-full ${level.className}`}>{level.label}</span>}
                </div>
                <div className={`font-bold text-lg mb-1 ${idx === 0 ? 'text-primary-700' : 'text-foreground-700'}`}>
                  {type?.name} ({code})
                </div>
                <div className="text-xs text-foreground-600 mb-2">{type?.description}</div>
                <div className={`text-xs font-semibold ${idx === 0 ? 'text-primary-600' : 'text-foreground-500'}`}>
                  상대 흥미 {scorePct}점
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-foreground-400 mb-2">6개 유형 중 최고점을 100으로 환산한 상대 흥미예요.</p>
        <div className="space-y-2">
          {Object.entries(hollandScores).sort((a, b) => b[1] - a[1]).map(([code, score]) => (
            <div key={code} className="flex items-center gap-3">
              <span className="text-xs text-foreground-600 w-16 flex-shrink-0">{hollandLabels[code]}</span>
              <div className="flex-1 bg-background-200 rounded-full h-2">
                <div
                  className="bg-primary-400 h-2 rounded-full transition-all"
                  style={{ width: `${maxHollandScore > 0 ? Math.min((score / maxHollandScore) * 100, 100) : 0}%` }}
                ></div>
              </div>
              <span className="text-xs text-foreground-500 w-10 text-right">{maxHollandScore > 0 ? Math.round((score / maxHollandScore) * 100) : 0}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Value Type */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
        <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
          <i className="ri-heart-line text-accent-500"></i>
          나의 직업 가치관
        </h2>
        <div className="flex flex-wrap gap-2">
          {topValues.map((v, idx) => (
            <div key={v} className={`px-4 py-2 rounded-xl text-sm font-medium ${
              idx === 0 ? 'bg-accent-100 text-accent-800 border border-accent-200' : 'bg-background-100 text-foreground-600 border border-background-200'
            }`}>
              {idx === 0 ? '⭐ ' : ''}{valueLabels[v] || v}
            </div>
          ))}
        </div>

        <div className="space-y-2 mt-4">
          {Object.entries(valueScores).sort((a, b) => b[1] - a[1]).map(([key, score]) => (
            <div key={key} className="flex items-center gap-3">
              <span className="text-xs text-foreground-600 w-16 flex-shrink-0">{valueLabels[key] || key}</span>
              <div className="flex-1 bg-background-200 rounded-full h-2">
                <div
                  className="bg-accent-400 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min((score / VALUE_MAX_SCORE) * 100, 100)}%` }}
                ></div>
              </div>
              <span className="text-xs text-foreground-500 w-10 text-right">{Math.round((score / VALUE_MAX_SCORE) * 100)}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* 전문 진로 분석 리포트 */}
      <CareerProReport report={proReport} />

      {/* Recommended Track */}
      <div className="bg-background-50 border border-primary-200 rounded-xl p-5 mb-4">
        <h2 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-road-map-line text-primary-500"></i>
          추천 계열 · 진로 방향
        </h2>

        {topTrack && (
          <div className={`${topTrack.bgColor} rounded-xl p-4 border ${topTrack.bgColor.replace('bg-', 'border-').replace('50', '200')} mb-3`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`font-bold text-lg ${topTrack.color}`}>⭐ {topTrack.name}</span>
              <span className="text-xs bg-primary-500 text-white px-2 py-1 rounded-full">주추천</span>
            </div>
            <p className="text-foreground-600 text-sm">{topTrack.description}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {otherTracks.map((track) => track && (
            <div key={track.id} className={`${track.bgColor} px-3 py-2 rounded-lg text-sm ${track.color} font-medium`}>
              {track.name}
            </div>
          ))}
        </div>
      </div>

      {/* Recommended Majors */}
      {recommendedMajors.length > 0 && (
        <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
          <h2 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-building-4-line text-secondary-500"></i>
            추천 전공학과
          </h2>
          <div className="space-y-3">
            {recommendedMajors.map((major) => (
              <div key={major.name} className="border border-background-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="font-semibold text-foreground-950">{major.name}</h3>
                  <button
                    onClick={() => navigate('/subject-guide')}
                    className="whitespace-nowrap text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full hover:bg-primary-200 cursor-pointer flex-shrink-0"
                  >
                    과목 보기
                  </button>
                </div>
                <p className="text-foreground-600 text-xs mb-3 leading-relaxed">{major.description}</p>

                {major.courses && major.courses.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs text-foreground-500 mb-2 flex items-center gap-1">
                      <i className="ri-book-2-line text-secondary-500"></i>
                      대학에서 배우는 전공과목
                    </div>
                    <div className="space-y-1.5">
                      {major.courses.map((course) => (
                        <div key={course.name} className="flex items-start gap-2">
                          <span className="text-secondary-500 mt-1 flex-shrink-0"><i className="ri-checkbox-circle-line text-xs"></i></span>
                          <div className="min-w-0">
                            <span className="text-xs font-medium text-foreground-800 whitespace-nowrap">{course.name}</span>
                            <span className="text-xs text-foreground-500"> · {course.description}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-xs text-foreground-500 mb-1">관련 직업</div>
                  <div className="flex flex-wrap gap-1.5">
                    {major.relatedJobs.slice(0, 4).map(job => (
                      <span key={job} className="text-xs bg-background-100 text-foreground-700 px-2 py-0.5 rounded-full">{job}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommended Jobs */}
      <ResultJobs jobs={allJobs} />

      {/* Recommended Subjects */}
      {topTrack && <ResultSubjects trackId={topTrack.id} trackName={topTrack.name} />}

      {/* Traits */}
      {hollandType1 && (
        <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-6">
          <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-star-line text-accent-500"></i>
            나의 강점 특성
          </h2>
          <div className="flex flex-wrap gap-2">
            {[...(hollandType1.traits || []), ...(hollandType2?.traits.slice(0, 2) || [])].map((trait) => (
              <span key={trait} className="text-sm bg-accent-50 text-accent-700 border border-accent-200 px-3 py-1.5 rounded-full">
                {trait}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Next Steps */}
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-5 mb-6">
        <h3 className="font-semibold text-primary-800 mb-3">
          <i className="ri-arrow-right-circle-line mr-2"></i>다음 단계
        </h3>
        <div className="space-y-2">
          <button
            onClick={() => navigate('/subject-guide')}
            className="whitespace-nowrap w-full text-left text-sm text-primary-700 bg-white border border-primary-200 rounded-lg px-4 py-2.5 hover:bg-primary-100 cursor-pointer flex items-center gap-2"
          >
            <i className="ri-book-open-line"></i>
            내 계열에 맞는 선택과목 상세 보기
          </button>
          <button
            onClick={() => navigate('/study-style')}
            className="whitespace-nowrap w-full text-left text-sm text-primary-700 bg-white border border-primary-200 rounded-lg px-4 py-2.5 hover:bg-primary-100 cursor-pointer flex items-center gap-2"
          >
            <i className="ri-brain-line"></i>
            나에게 맞는 공부법 찾기
          </button>
        </div>
      </div>

      {/* Retake Button */}
      <button
        onClick={onRetake}
        className="whitespace-nowrap w-full py-3.5 rounded-xl border border-background-300 text-foreground-600 font-semibold text-sm hover:bg-background-100 transition-colors cursor-pointer"
      >
        <i className="ri-refresh-line mr-2"></i>다시 검사하기
      </button>
    </div>
  );
}